import { UserInfo } from '@microsoft/teamsfx';
import { DateTime } from 'luxon';

import { EnabledTools } from 'src/models/Profile';
import { CumulativePointsProgramParticipantStatus, ProgramParticipantStatus } from 'src/models/rewards/Rewards';
import { DAILY_HABIT_EXPIRED_IMAGE_PATHS, ERROR_MESSAGES, THOUSAND_UNIT } from './constants';
import { AvailabilityState, GatewayRequirementState } from './enums';

export function openExternalUrl(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

export const getNormalizedApiEndpoint = (path: string): string => {
  const baseUrl = process.env.REACT_APP_WHS_SERVICE_API_ENDPOINT;

  if (!baseUrl || !path) throw new Error(ERROR_MESSAGES.invalidApiEndpoint);

  return `${baseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
};

export const ensureValidEndpoint = (endpoint: string | undefined): string => {
  if (!endpoint) throw new Error(ERROR_MESSAGES.invalidApiEndpoint);

  try {
    return new URL(endpoint).toString();
  }
  catch {
    throw new Error(ERROR_MESSAGES.invalidApiEndpoint);
  }
};

export const getToolName = (toolId: string, enabledTools?: EnabledTools): string => {
  return enabledTools?.[toolId]?.toolName || '';
};

export const getTeamUserProperties = (teamUser: UserInfo | null | undefined): UserInfo => {
  return teamUser ?? {
    displayName: 'Unknown',
    objectId: 'Unknown',
    tenantId: 'Unknown',
    preferredUserName: 'Unknown',
  };
};

export const formatRequiredPoints = (requiredPoint: number): string | number =>
  requiredPoint >= 1000 ? `${requiredPoint / 1000}${THOUSAND_UNIT}` : requiredPoint;

export const formatISODateAsDateWithSlashes = (isoDate: string): string => {
  return DateTime.fromISO(isoDate, { setZone: true }).toFormat('M/d/yyyy');
};

export const formatProgramStatusVisibleDate = ({ programTypeStatus, startDate, endDate }: ProgramParticipantStatus): string => {
  let rawDate: string | null;

  switch (programTypeStatus.state) {
    case AvailabilityState.Completed:
      rawDate = programTypeStatus.completedDate;
      break;
    case AvailabilityState.NotAvailableYet:
      rawDate = startDate;
      break;
    default:
      rawDate = endDate;
  }

  if (!rawDate) {
    throw new Error('Unexpected null for date');
  }

  return formatISODateAsDateWithSlashes(rawDate);
};

export const availabilityStateCallToAction = (state: AvailabilityState, pluralRewardsName = 'Rewards'): string => {
  const callToActionMap: Record<AvailabilityState, string> = {
    [AvailabilityState.NotAvailableYet]: 'View Details',
    [AvailabilityState.Available]: 'Continue',
    [AvailabilityState.Completed]: `View ${pluralRewardsName}`,
    [AvailabilityState.Expired]: `View ${pluralRewardsName}`,
  };

  const action = callToActionMap[state];
  if (!action) {
    throw new Error(`Unknown AvailabilityState: ${state}`);
  }

  return action;
};

export const cumulativePointsCallToActionMessage = (programTypeStatus: CumulativePointsProgramParticipantStatus): string => {
  const pluralRewardsName = programTypeStatus.programTypeStatus.rewardsNames.plural;

  switch (programTypeStatus.programTypeStatus.gatewayRequirementState) {
    case GatewayRequirementState.RequirementsNotAvailableYet:
    case GatewayRequirementState.RequirementsNotComplete:
      return `Unlock Program`;
    case GatewayRequirementState.RequirementsFailed:
    case GatewayRequirementState.RequirementsExpired:
      return `View ${pluralRewardsName}`;
    default:
      return availabilityStateCallToAction(programTypeStatus.programTypeStatus.state, pluralRewardsName);
  }
};

export const isParticipantProgramEnded = (state: AvailabilityState): boolean => {
  return state === AvailabilityState.Completed || state === AvailabilityState.Expired;
};

export const getWebMdPage = (path: string): string => {
  const webMdPage = process.env.REACT_APP_WEBMD_PAGE;

  if (!webMdPage || !path) throw new Error(ERROR_MESSAGES.invalidApiEndpoint);

  return `${webMdPage.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
};

/**
 * Format a list of names into a readable string with quotes and "and" before the last name.
 * Examples:
 *   ["A"] => '"A"'
 *   ["A", "B"] => '"A" and "B"'
 *   ["A", "B", "C"] => '"A", "B" and "C"'
 */
export const formatNameList = (names: string[] = []): string => {
  const quotedNames = names.map(name => `"${name}"`);

  if (quotedNames.length === 0) return '';
  if (quotedNames.length === 1) return quotedNames[0];
  if (quotedNames.length === 2) return `${quotedNames[0]} and ${quotedNames[1]}`;

  const last = quotedNames.pop();
  return `${quotedNames.join(', ')} and ${last}`;
};

export const getInterstitialImageUrl = (
  completedCount: number,
  failedCount: number,
): string => {
  const totalPlans = completedCount + failedCount;
  if (totalPlans === 0) return '';

  // All plans completed
  if (failedCount === 0) {
    return DAILY_HABIT_EXPIRED_IMAGE_PATHS.COMPLETED_ALL;
  }

  // All plans failed
  if (completedCount === 0) {
    return DAILY_HABIT_EXPIRED_IMAGE_PATHS.INCOMPLETE_ALL;
  }

  // Mix of completed and failed
  const completionRate = completedCount / totalPlans;
  if (completionRate >= 0.5) {
    return DAILY_HABIT_EXPIRED_IMAGE_PATHS.COMPLETED_MOST;
  }

  return DAILY_HABIT_EXPIRED_IMAGE_PATHS.INCOMPLETE_MOST;
};

export const getWebMDLogoImg = (themeString: string): string => {
  switch (themeString) {
    case 'dark':
    case 'contrast':
      return '/images/WebMDLogo_Dark.svg';
    default:
      return '/images/WebMDLogo_Light.svg';
  }
};
